import type { PlayerNameFields, UserPreferences } from '../types';

export type LineupSortablePlayer = PlayerNameFields & {
  player_tier?: string | number | null;
  estimated_dynamic_rating?: number | string | null;
  availability_status?: string | null;
};

export function lineupTierKey(playerTier: string | number | null | undefined): string {
  const match = String(playerTier ?? '').trim().toLowerCase().match(/^(?:tier[_\s-]*)?([1-4])$/);
  return match ? `tier_${match[1]}` : '';
}

export function isLineupSelectablePlayer(player: LineupSortablePlayer): boolean {
  return player.availability_status !== 'not_available';
}

export function compareLineupSelectablePlayers<T extends LineupSortablePlayer>(
  a: T,
  b: T,
  preferences: UserPreferences,
): number {
  const aTier = lineupTierKey(a.player_tier);
  const bTier = lineupTierKey(b.player_tier);
  const tierCompare = (aTier ? Number(aTier.slice(-1)) : 5) - (bTier ? Number(bTier.slice(-1)) : 5);
  if (tierCompare) return tierCompare;

  const availabilityCompare = availabilityRank(a.availability_status) - availabilityRank(b.availability_status);
  if (availabilityCompare) return availabilityCompare;

  const aEdr = numericEdr(a.estimated_dynamic_rating);
  const bEdr = numericEdr(b.estimated_dynamic_rating);
  if (aEdr === null && bEdr !== null) return 1;
  if (aEdr !== null && bEdr === null) return -1;
  if (aEdr !== null && bEdr !== null && aEdr !== bEdr) return bEdr - aEdr;

  const firstCompare = (a.first_name || '').localeCompare(b.first_name || '');
  const lastCompare = (a.last_name || '').localeCompare(b.last_name || '');
  return preferences.playerSortOrder === 'first_name'
    ? firstCompare || lastCompare
    : lastCompare || firstCompare;
}

function availabilityRank(status: string | null | undefined): number {
  return status === 'available' ? 0 : 1;
}

function numericEdr(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}
